import type { Layer, TextLayer, ImageLayer, ShapeLayer } from '../schema/layers.js';
import type { CanonicalDocument } from '../schema/canonical-document.js';
import type { PointMm } from '../schema/primitives.js';
import { applyPoint, multiply, fromTranslation, decompose } from './affine.js';
import { bboxOfPoints, bboxUnion, type BBoxMm } from './polygon.js';
import { pathPoints } from './path.js';

/**
 * Anchura media de glifo como fraccion del cuerpo, para ESTIMAR la caja de un
 * texto que aun no se ha congelado. Es una estimacion declarada, no una medida:
 * cualquier diagnostico que dependa de ella se marca `estimated: true`. En
 * cuanto el texto tiene `frozenLayout`, se usa la geometria real de los glifos.
 */
export const ESTIMATED_GLYPH_WIDTH_RATIO = 0.55;

export interface LayerOutline {
  /** Contorno en mm, ya en el espacio de la zona de marcaje. */
  points: PointMm[];
  bbox: BBoxMm;
  /** true si la geometria proviene de una estimacion y no de una medida. */
  estimated: boolean;
}

function localOutline(layer: Layer): { points: PointMm[]; estimated: boolean } {
  switch (layer.kind) {
    case 'image':
      return { points: imageLocalOutline(layer), estimated: false };
    case 'shape':
      return { points: shapeLocalOutline(layer), estimated: false };
    case 'text':
      return textLocalOutline(layer);
  }
}

function imageLocalOutline(_layer: ImageLayer): PointMm[] {
  // Espacio local de una imagen: rectangulo UNIDAD (ver affine.ts, D-I-01).
  return [
    { x: 0, y: 0 },
    { x: 1, y: 0 },
    { x: 1, y: 1 },
    { x: 0, y: 1 },
  ];
}

function shapeLocalOutline(layer: ShapeLayer): PointMm[] {
  const pts = pathPoints(layer.path);
  if (pts.length === 0) return [{ x: 0, y: 0 }];
  const half = (layer.strokeWidthMm ?? 0) / 2;
  if (half === 0) return pts;
  // El trazo ensancha la forma: la caja crece medio grosor por cada lado.
  const b = bboxOfPoints(pts);
  return [
    { x: b.minX - half, y: b.minY - half },
    { x: b.maxX + half, y: b.minY - half },
    { x: b.maxX + half, y: b.maxY + half },
    { x: b.minX - half, y: b.maxY + half },
  ];
}

function textLocalOutline(layer: TextLayer): { points: PointMm[]; estimated: boolean } {
  const frozen = layer.frozenLayout;
  if (frozen && frozen.glyphs.length > 0) {
    const pts: PointMm[] = [];
    for (const g of frozen.glyphs) {
      pts.push({ x: g.xMm, y: g.yMm - layer.sizeMm });
      pts.push({ x: g.xMm + layer.sizeMm * ESTIMATED_GLYPH_WIDTH_RATIO, y: g.yMm });
    }
    const b = bboxOfPoints(pts);
    return {
      points: [
        { x: b.minX, y: b.minY },
        { x: b.maxX, y: b.minY },
        { x: b.maxX, y: b.maxY },
        { x: b.minX, y: b.maxY },
      ],
      estimated: false,
    };
  }

  const lines = layer.text.split('\n');
  const advance = layer.sizeMm * ESTIMATED_GLYPH_WIDTH_RATIO + layer.tracking;
  let width = 0;
  for (const line of lines) {
    const chars = [...line].length;
    const w = chars === 0 ? 0 : chars * advance - layer.tracking;
    if (w > width) width = w;
  }
  const height = Math.max(1, lines.length) * layer.lineHeightMm;
  return {
    points: [
      { x: 0, y: 0 },
      { x: width, y: 0 },
      { x: width, y: height },
      { x: 0, y: height },
    ],
    estimated: true,
  };
}

/**
 * Traslacion que lleva el punto de anclaje al origen del espacio local, antes
 * de aplicar la matriz de la capa.
 */
function anchorOffset(layer: Layer, local: PointMm[]): PointMm {
  switch (layer.anchor) {
    case 'top-left': {
      const b = bboxOfPoints(local);
      return { x: -b.minX, y: -b.minY };
    }
    case 'center': {
      const b = bboxOfPoints(local);
      return { x: -(b.minX + b.maxX) / 2, y: -(b.minY + b.maxY) / 2 };
    }
    case 'custom': {
      const p = layer.anchorPoint ?? { x: 0, y: 0 };
      return { x: -p.x, y: -p.y };
    }
  }
}

/** Contorno de la capa en mm sobre la zona de marcaje. */
export function layerOutlineMm(layer: Layer): LayerOutline {
  const { points: local, estimated } = localOutline(layer);
  const off = anchorOffset(layer, local);
  const m = multiply(layer.transform, fromTranslation(off.x, off.y));
  const points = local.map((p) => applyPoint(m, p));
  return { points, bbox: bboxOfPoints(points), estimated };
}

export function layerBBoxMm(layer: Layer): BBoxMm {
  return layerOutlineMm(layer).bbox;
}

export function documentBBoxMm(doc: CanonicalDocument): BBoxMm | null {
  let acc: BBoxMm | null = null;
  for (const layer of doc.layers) {
    const b = layerBBoxMm(layer);
    acc = acc === null ? b : bboxUnion(acc, b);
  }
  return acc;
}

/**
 * Resolucion efectiva de una imagen colocada, en puntos por pulgada REALES:
 * los pixeles que tiene el fichero repartidos sobre los milimetros que ocupa
 * impresa. Es la unica cifra que importa para decidir si una imagen "vale".
 */
export function effectiveDpi(layer: ImageLayer): { horizontal: number; vertical: number; min: number } {
  const d = decompose(layer.transform);
  const widthMm = Math.abs(d.scaleX);
  const heightMm = Math.abs(d.scaleY);
  const horizontal = widthMm > 0 ? (layer.intrinsic.widthPx / widthMm) * 25.4 : Infinity;
  const vertical = heightMm > 0 ? (layer.intrinsic.heightPx / heightMm) * 25.4 : Infinity;
  return { horizontal, vertical, min: Math.min(horizontal, vertical) };
}

/** Grosor de trazo real tras aplicar la matriz (una escala 0.1 adelgaza el trazo). */
export function effectiveStrokeWidthMm(layer: ShapeLayer): number | null {
  if (layer.strokeWidthMm === undefined) return null;
  const d = decompose(layer.transform);
  const scale = (Math.abs(d.scaleX) + Math.abs(d.scaleY)) / 2;
  return layer.strokeWidthMm * scale;
}

/** Cuerpo de texto real tras aplicar la matriz. */
export function effectiveTextSizeMm(layer: TextLayer): number {
  const d = decompose(layer.transform);
  return layer.sizeMm * Math.min(Math.abs(d.scaleX), Math.abs(d.scaleY));
}

/**
 * A que TAMANO se imprime bien esta imagen.
 *
 * El aviso de resolucion dice «39 ppp, exige 150», que es correcto pero no
 * accionable: el cliente no sabe que hacer con esa cifra. «Esta imagen llega
 * bien hasta 78 x 52 mm» si lo dice, y ademas permite ofrecer el boton que la
 * ajusta a esa medida.
 *
 * Es el mismo calculo de `effectiveDpi` despejado al reves, asi que nunca puede
 * contradecir al aviso: si se ajusta a esta medida, el aviso desaparece.
 */
export function medidaMaximaMm(
  intrinsic: { widthPx: number; heightPx: number },
  dpiMinimo: number,
): { widthMm: number; heightMm: number } {
  if (dpiMinimo <= 0) return { widthMm: Infinity, heightMm: Infinity };
  return {
    widthMm: (intrinsic.widthPx / dpiMinimo) * 25.4,
    heightMm: (intrinsic.heightPx / dpiMinimo) * 25.4,
  };
}

/**
 * Escala que hay que aplicar a una capa de imagen para que cumpla el minimo.
 *
 * Devuelve 1 cuando ya cumple: nunca AMPLIA una imagen para «aprovechar» que le
 * sobra resolucion, porque agrandar el diseno no es lo que ha pedido nadie.
 */
export function escalaParaCumplirDpi(layer: ImageLayer, dpiMinimo: number): number {
  const dpi = effectiveDpi(layer);
  if (!Number.isFinite(dpi.min) || dpi.min >= dpiMinimo) return 1;
  return dpi.min / dpiMinimo;
}
